// src/lib/collectJsTokenizer.ts
//
// The browser half of `tokenize_with_public_key`, for the gateway that
// protocol belongs to.
//
// ============================================================================
// THIS IS NOT A PROCESSOR CHOICE
// ============================================================================
//
// Payments selects the protocol; this module executes the one it selected.
// Nothing here is reachable from a user preference, a feature flag or a
// `preferredProcessor` field — the dialog switches on `browserAction.kind`,
// which arrives from Payments, and there is no branch a person can influence.
//
// ============================================================================
// NO CARD VALUE EVER EXISTS IN CRM
// ============================================================================
//
// The three card fields below are IFRAMES served by the gateway. CRM supplies
// only a CSS selector saying where each iframe should mount; it renders no
// `<input>`, registers no input handler, and reads no `.value`. The customer's
// PAN, expiry and CVV are typed into another origin's document, which CRM's
// JavaScript cannot read even in principle — that is the entire reason for
// using hosted fields rather than a form CRM controls.
//
// What comes back is a single-use token. It is returned to the caller and
// never logged, stored, or sent anywhere except straight on to Payments.

/** The gateway's tokenizer, pinned. Not configurable — a settable script URL would be an injection point. */
const COLLECT_JS_URL = 'https://secure.nmi.com/token/Collect.js';

/** Where each hosted iframe mounts. Selectors only; CRM owns the box, not the contents. */
export const CARD_FIELD_SELECTORS = Object.freeze({
  number: '#levelup-card-number',
  expiry: '#levelup-card-expiry',
  securityCode: '#levelup-card-security-code',
});

/**
 * How the gateway's own input should look INSIDE its iframe.
 *
 * ============================================================================
 * WHY THIS EXISTS: THE BOX INSIDE THE BOX
 * ============================================================================
 *
 * Each field is drawn by three layers, and until now two of them drew a box:
 *
 *   1. CRM's mount div  -- border, radius, background, horizontal padding.
 *   2. The iframe ELEMENT -- a child of that div, in CRM's document. Tailwind's
 *      preflight already neutralises it (`*` sets `border-width: 0`, and
 *      `iframe` is set to `display: block`), so this layer draws nothing.
 *   3. The gateway's `<input>`, inside the iframe -- ANOTHER document. Preflight
 *      cannot reach it, `styleSniffer: false` deliberately stops CRM's
 *      stylesheet being copied in, and nothing was passed to replace either.
 *      So it rendered at user-agent defaults: its own border, its own
 *      background. That was the inner box.
 *
 * `customCss` is the gateway's supported way to style that input -- CSS handed
 * to the gateway to apply inside its own frame. It is NOT CRM reaching across
 * an origin: CRM still cannot read the field, and nothing here touches a value.
 * Presentation only.
 *
 * The choice is deliberate: CRM keeps the visible boundary (layer 1) and the
 * provider's input goes transparent, rather than the reverse. CRM's shell is
 * the layer that can match the rest of the form, respond to layout, and carry
 * a focus ring the surrounding design system already defines.
 */
/**
 * THE HEIGHT THAT MAKES THE FIELD CLICKABLE.
 *
 * Collect.js sizes each hosted iframe to fit its own input and writes that
 * height INLINE on the iframe element -- measured against the real library:
 * `style="display: block; height: 17px;"`. An inline style beats any
 * first-party stylesheet rule, so CRM cannot size the frame from the outside.
 * The only lever is the input, through the provider's supported `customCss`.
 *
 * With no explicit height the input was ~17px at the TOP of a 36px shell, and
 * the bottom half of the visible field was not the provider's frame at all. A
 * click on the centre -- the natural place to click -- landed on CRM's own div
 * and the card could not be typed into. Measured, with a real pointer click:
 *
 *   height: auto  -> frame 17px, centre click accepted 0 characters
 *   height: 100%  -> frame 16px, centre click accepted 0 characters
 *   height: 34px  -> frame 34px, centre click accepted characters in all three
 *
 * A percentage does not work: the frame document has no height on html/body,
 * so it resolves to auto. It has to be explicit pixels.
 *
 * NO `line-height`. It was in the first working variant and is not needed --
 * an input centres its text in its content box already, and the harness passes
 * without it.
 */
const SHELL_HEIGHT_PX = 36; // Tailwind h-9 on the shell in HostedCardFields.
const SHELL_BORDER_PX = 1; // border-* top and bottom, inside border-box.
/** The shell's CONTENT box -- what the provider's frame has to fill. */
export const HOSTED_FIELD_CONTENT_HEIGHT_PX = SHELL_HEIGHT_PX - SHELL_BORDER_PX * 2;

const FIELD_TEXT = '#0f172a'; // slate-900, matching the form's body text
const FIELD_PLACEHOLDER = '#94a3b8'; // slate-400
const FIELD_INVALID = '#be123c'; // rose-700, matching the dialog's error copy
// Named explicitly because `styleSniffer: false` means nothing is inherited.
const FIELD_FONT =
  'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const HOSTED_FIELD_CSS = Object.freeze({
  /**
   * Resting state. Every box-drawing property is explicitly surrendered so the
   * only boundary a customer sees is CRM's shell.
   */
  custom: Object.freeze({
    border: 'none',
    outline: 'none',
    'box-shadow': 'none',
    'background-color': 'transparent',
    margin: '0',
    padding: '0',
    width: '100%',
    // See HOSTED_FIELD_CONTENT_HEIGHT_PX. This single declaration is what makes
    // the whole visible field clickable, because the provider sizes its frame
    // to this input.
    //
    // `position: absolute; inset: 0` was here and did NOT apply: the provider
    // allowlists the properties it accepts, and the real frame reported
    // `position: static; inset: auto`. It has been removed rather than left in
    // place looking as though it stretches anything.
    height: `${HOSTED_FIELD_CONTENT_HEIGHT_PX}px`,
    'font-family': FIELD_FONT,
    'font-size': '14px',
    color: FIELD_TEXT,
  }),
  /**
   * Focus is indicated ONCE, by the shell. The inner outline is removed so
   * focus does not reintroduce the second box it was the whole point of
   * removing.
   *
   * The shell's treatment is driven by `useHostedFieldFocus`, NOT by
   * `:focus-within` -- see that hook for why the CSS approach cannot work.
   */
  focus: Object.freeze({ outline: 'none', 'box-shadow': 'none' }),
  placeholder: Object.freeze({ color: FIELD_PLACEHOLDER }),
  /** Invalid input recolours the TEXT. A red box here would be a second box. */
  invalid: Object.freeze({ color: FIELD_INVALID }),
  valid: Object.freeze({ color: FIELD_TEXT }),
});

export type TokenizationOutcome =
  | { ok: true; paymentToken: string }
  | { ok: false; reason: 'unavailable' | 'invalid_card' | 'cancelled'; detail?: string };

interface CollectJsGlobal {
  configure: (options: Record<string, unknown>) => void;
  startPaymentRequest: () => void;
}

declare global {
  interface Window {
    CollectJS?: CollectJsGlobal;
  }
}

/**
 * Load the tokenizer exactly once per page.
 *
 * The tokenization key is a PUBLIC browser credential and travels as a script
 * attribute, which is how the gateway expects it. Browser-safe is not the same
 * as log-safe, so it is never written anywhere else.
 */
let loading: Promise<CollectJsGlobal | null> | undefined;

export function loadTokenizer(tokenizationKey: string): Promise<CollectJsGlobal | null> {
  if (loading) return loading;

  loading = new Promise<CollectJsGlobal | null>((resolve) => {
    if (window.CollectJS) {
      resolve(window.CollectJS);
      return;
    }
    if (!tokenizationKey) {
      resolve(null);
      return;
    }

    const script = document.createElement('script');
    script.src = COLLECT_JS_URL;
    script.async = true;
    script.setAttribute('data-tokenization-key', tokenizationKey);
    script.onload = () => resolve(window.CollectJS ?? null);
    // A blocked or failed script is "unavailable", not "declined". The
    // difference matters: one is worth retrying, the other is not.
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });

  return loading;
}

/** Test seam. The loader memoizes per page; a test needs a fresh page each time. */
export function resetTokenizerForTests(): void {
  loading = undefined;
}

/**
 * Mount the hosted fields and wait for the customer to submit.
 *
 * Resolves when the gateway hands back a token, or when it refuses. It does
 * NOT resolve merely because the fields mounted — the customer has to type and
 * submit, and `submit()` in the returned handle is what asks the gateway to
 * validate and tokenize.
 */
export interface TokenizerHandle {
  /** Ask the gateway to validate the mounted fields and issue a token. */
  submit: () => void;
  /** Resolves once the gateway answers. */
  result: Promise<TokenizationOutcome>;
}

/** How long to wait for the gateway to report its fields ready. */
const FIELDS_READY_TIMEOUT_MS = 15_000;

export async function mountCardFields(args: {
  tokenizationKey: string;
  onFieldValidity?: (allValid: boolean) => void;
}): Promise<TokenizerHandle | null> {
  const tokenizer = await loadTokenizer(args.tokenizationKey);
  if (!tokenizer) return null;

  let settle: (outcome: TokenizationOutcome) => void = () => {};
  const result = new Promise<TokenizationOutcome>((resolve) => {
    settle = resolve;
  });

  /**
   * Which fields the gateway currently considers valid.
   *
   * Validity is a boolean per field name. It is NOT the field's contents, and
   * the gateway reports nothing else — there is no callback anywhere in this
   * module that receives a card value.
   */
  const valid: Record<string, boolean> = {};

  // ── Readiness is the GATEWAY's to declare, not ours to assume ──────
  //
  // Collect.js fires `fieldsAvailableCallback` when its hosted inputs actually
  // exist and are usable. Without it the only readiness signal we had was our
  // own mount elements rendering — which they do whether or not the gateway
  // ever finished. That is how a card form that could not be typed into was
  // presented to a user as ready, and how two rounds of tests certified it.
  //
  // So the handle is withheld until the gateway says the fields are there. No
  // signal within the timeout is treated as UNAVAILABLE, not as ready.
  let announceReady: () => void = () => {};
  const fieldsReady = new Promise<boolean>((resolve) => {
    announceReady = () => resolve(true);
    setTimeout(() => resolve(false), FIELDS_READY_TIMEOUT_MS);
  });

  try {
    tokenizer.configure({
      fieldsAvailableCallback: () => announceReady(),
      variant: 'inline',
      // CRM's own stylesheet must not reach into the gateway's iframes.
      // Unchanged -- and it is exactly why the explicit CSS below is needed.
      styleSniffer: false,
      customCss: HOSTED_FIELD_CSS.custom,
      focusCss: HOSTED_FIELD_CSS.focus,
      placeholderCss: HOSTED_FIELD_CSS.placeholder,
      invalidCss: HOSTED_FIELD_CSS.invalid,
      validCss: HOSTED_FIELD_CSS.valid,
      fields: {
        ccnumber: { selector: CARD_FIELD_SELECTORS.number, placeholder: '•••• •••• •••• ••••' },
        ccexp: { selector: CARD_FIELD_SELECTORS.expiry, placeholder: 'MM / YY' },
        cvv: { selector: CARD_FIELD_SELECTORS.securityCode, placeholder: '•••' },
      },
      validationCallback: (field: string, status: boolean) => {
        valid[field] = status;
        args.onFieldValidity?.(Object.values(valid).length === 3 && Object.values(valid).every(Boolean));
      },
      timeoutDuration: 15000,
      timeoutCallback: () =>
        settle({ ok: false, reason: 'unavailable', detail: 'the card form timed out' }),
      callback: (response: { token?: unknown }) => {
        // The one place a token exists in CRM. It is handed straight back and
        // is not assigned to anything that outlives this call.
        if (typeof response?.token === 'string' && response.token.length > 0) {
          settle({ ok: true, paymentToken: response.token });
          return;
        }
        settle({ ok: false, reason: 'invalid_card' });
      },
    });
  } catch {
    return null;
  }

  // Fail closed: no readiness signal means no usable form.
  if (!(await fieldsReady)) return null;

  return {
    submit: () => {
      try {
        tokenizer.startPaymentRequest();
      } catch {
        settle({ ok: false, reason: 'unavailable', detail: 'the card form could not be submitted' });
      }
    },
    result,
  };
}
